import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "hr-flash-theme";
const EVENT = "hr-flash-theme-change";

type Theme = "dark" | "light";

function getStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" ? "light" : "dark";
}

export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.classList.remove("light");
  } else {
    root.classList.remove("dark");
    root.classList.add("light");
  }
}

/**
 * Shared theme hook. Reads/writes once to localStorage and broadcasts changes
 * so every mounted component (Sidebar, SettingsPage, etc.) stays in sync and
 * the theme does not flip when navigating between pages.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());

  useEffect(() => {
    const sync = () => setThemeState(getStoredTheme());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
    window.dispatchEvent(new Event(EVENT));
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return { theme, isDarkMode: theme === "dark", setTheme, toggleTheme };
}

export function initTheme() {
  applyTheme(getStoredTheme());
}
